import React, { useState } from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { walkthroughCopy } from '../../content/walkthrough';

// Small square magnifier — same shape used on the login wordmark, sized down to match
// the previous lucide <Search /> footprint (w-5 h-5).
const SquareMagnifier = ({ className = 'w-5 h-5 text-cyan-400' }) => (
  <svg
    viewBox="0 0 250 250"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Search.Inc magnifier"
    data-testid="walkthrough-square-magnifier"
  >
    <path
      fill="currentColor"
      d="M425.766,376.984C431.396,376.391 506.547,376.391 521.958,376.984C539.276,377.651 591.344,381.417 607.021,383.135C618.604,384.406 622.078,385.188 620.995,386.266C620.073,387.188 608.698,387.703 577.557,388.24C545.042,388.792 531.495,389.292 513.755,390.573C489.807,392.307 479.844,393.526 473.276,395.536C456.172,400.771 447.62,414.234 446.792,437.219C446.349,449.516 448.109,459.875 451.995,467.849C454.995,474.01 458.771,477.26 465.943,479.865C472.125,482.115 477.115,482.661 495.307,483.094C521.448,483.714 536.885,482.745 544.938,479.974C554.714,476.604 562.75,468.458 566.536,458.073C569.177,450.833 569.62,446.964 570.125,426.708C570.573,408.573 570.823,405.651 572.109,403.24C573.781,400.115 579.563,399.313 598.068,399.635C608.609,399.823 612.281,400.052 613.443,400.609L614.964,401.344L614.875,457.932C614.802,504.818 614.667,514.646 614.094,515.224C613.708,515.604 612.052,516.167 610.406,516.469C604.943,517.479 443.417,516.323 425.62,515.146C419.438,514.734 417.161,514.068 415.594,512.203L414.344,510.719L414.349,450.125C414.349,410.245 414.536,388.396 414.891,386.214C415.188,384.385 415.885,382.151 416.443,381.25C417.802,379.047 421.276,377.458 425.766,376.984ZM571.823,520.542C573.578,519.833 574.484,519.938 577.974,521.255C590.995,526.156 612.057,539.594 634.854,557.531C644.245,564.917 654.766,574.391 656.714,577.198C660.995,583.37 660.813,592.563 656.281,599.146C653.87,602.661 648.604,607.214 644.953,608.943C642.125,610.281 641.021,610.526 637.766,610.51C634.568,610.495 633.365,610.229 630.594,608.927C626.01,606.766 619.505,601.729 615.214,597.026C611.193,592.615 588.599,561.151 581.932,550.682C570.417,532.589 567.026,522.479 571.823,520.542Z"
      transform="translate(-414, -376)"
    />
  </svg>
);

const Panel = ({ children }) => (
  <div
    className="relative z-10 w-full max-w-3xl mx-3 sm:mx-4 rounded-2xl max-h-[90vh] flex flex-col"
    style={{
      backgroundColor: 'rgba(8, 12, 24, 0.98)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 200, 255, 0.15)',
    }}
    data-testid="walkthrough-panel"
  >
    {children}
  </div>
);

const Step1 = ({ onNext }) => {
  const c = walkthroughCopy.step1of2;
  return (
    <Panel>
      <div className="overflow-y-auto p-6 sm:p-10 policy-content" data-testid="walkthrough-step-1">
        <div className="relative flex items-center gap-3 mb-4">
          <img
            src="/searchinc-logo.png"
            alt="Search.Inc — Actually not evil"
            style={{ height: 42 }}
            className="flex-shrink-0 select-none"
            draggable={false}
            data-testid="walkthrough-step-1-brand-logo"
          />
          <span className="absolute top-0 right-0 text-xs uppercase tracking-[0.2em] text-cyan-300/80" data-testid="walkthrough-step-1-eyebrow">Step 1 of 2</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-5 leading-tight">{c.heading}</h1>

        {c.body.map((para, i) => (
          <p key={i} className="mb-4 text-gray-100 leading-relaxed text-lg">
            {para}
          </p>
        ))}

        <h2 className="text-xl font-bold mt-8 mb-3 text-cyan-300">{c.whyWeExist.heading}</h2>
        <p className="mb-4 text-gray-100 leading-relaxed text-lg">{c.whyWeExist.body}</p>

        <h2 className="text-xl font-bold mt-8 mb-3 text-cyan-300">{c.firstPrinciples.heading}</h2>
        <ul className="space-y-2 mb-6">
          {c.firstPrinciples.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-gray-100 leading-relaxed text-lg">
              <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-1" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-white/10 px-6 py-4 sm:px-10 flex items-center justify-between bg-black/20">
        <span
          className="text-xs uppercase tracking-[0.2em] text-cyan-300/80"
          data-testid="walkthrough-step-1-progress-label"
        >
          Step 1 of 2
        </span>
        <button
          onClick={onNext}
          data-testid="walkthrough-step-1-next"
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {c.ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </Panel>
  );
};

const Step2 = ({ onNext, onBack }) => {
  const c = walkthroughCopy.step2of2;
  return (
    <Panel>
      <div className="overflow-y-auto p-6 sm:p-10 policy-content" data-testid="walkthrough-step-2">
        <div className="relative flex items-center gap-3 mb-4">
          <img
            src="/searchinc-logo.png"
            alt="Search.Inc — Actually not evil"
            style={{ height: 42 }}
            className="flex-shrink-0 select-none"
            draggable={false}
            data-testid="walkthrough-step-2-brand-logo"
          />
          <span className="absolute top-0 right-0 text-xs uppercase tracking-[0.2em] text-cyan-300/80" data-testid="walkthrough-step-2-eyebrow">Step 1 of 2 (Continued)</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-cyan-400 mb-5 leading-tight">{c.heading}</h1>

        <p className="mb-2 text-gray-100 leading-relaxed text-lg">{c.intro}</p>
        <ul className="space-y-1 mb-5 ml-1">
          {c.versions.map((v, i) => (
            <li key={i} className="flex gap-2 text-gray-100 leading-relaxed text-lg">
              <span aria-hidden="true" className="flex-shrink-0">•</span>
              <span className="flex-1">{v}</span>
            </li>
          ))}
        </ul>

        {Array.isArray(c.pricingNote) ? (
          c.pricingNote.map((line, i) => (
            <p key={i} className="mb-3 text-white leading-relaxed text-lg">{line}</p>
          ))
        ) : (
          <p className="mb-6 text-white leading-relaxed text-lg">{c.pricingNote}</p>
        )}
        <div className="mb-3" />

        <h2 className="text-lg font-bold mt-6 mb-2 text-cyan-300">{c.exploreHeading}</h2>
        <ul className="space-y-1 mb-2 ml-1">
          {c.exploreBullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-gray-100 leading-relaxed text-lg">
              <span aria-hidden="true" className="flex-shrink-0">•</span>
              <span className="flex-1">{b}</span>
            </li>
          ))}
        </ul>
        {c.exploreNote ? <p className="mb-6 text-gray-300 italic text-lg">{c.exploreNote}</p> : null}

        <div className="my-6 p-4 rounded-lg border border-cyan-400/30 bg-cyan-400/5">
          <p className="text-gray-100 leading-relaxed text-lg">{c.mvpNote}</p>
        </div>

        <h2 className="text-lg font-bold mt-6 mb-2 text-cyan-300">{c.realHeading}</h2>
        <ul className="space-y-1 mb-5 ml-1">
          {c.realBullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-gray-100 leading-relaxed text-lg">
              <span aria-hidden="true" className="flex-shrink-0">•</span>
              <span className="flex-1">{b}</span>
            </li>
          ))}
        </ul>

        <p className="mb-4 text-gray-100 leading-relaxed text-lg">{c.sessionNote}</p>
      </div>

      <div className="border-t border-white/10 px-6 py-4 sm:px-10 flex items-center justify-between bg-black/20 gap-3">
        <button
          onClick={onBack}
          data-testid="walkthrough-step-2-back"
          className="text-gray-300 hover:text-white transition-colors text-sm"
        >
          ← Back
        </button>
        <span
          className="text-xs uppercase tracking-[0.2em] text-cyan-300/80 ml-8"
          data-testid="walkthrough-step-2-progress-label"
        >
          Step 1 of 2 (Continued)
        </span>
        <button
          onClick={onNext}
          data-testid="walkthrough-step-2-next"
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {c.ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </Panel>
  );
};

export const Walkthrough = ({ onComplete }) => {
  const [step, setStep] = useState(1);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center py-6 sm:py-10"
      style={{
        background: 'radial-gradient(ellipse at 30% 20%, rgba(34,211,238,0.08) 0%, transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(120,60,180,0.12) 0%, transparent 60%), #000',
      }}
      data-testid="walkthrough-overlay"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm pointer-events-none" />
      {step === 1 && <Step1 onNext={() => setStep(2)} />}
      {step === 2 && <Step2 onBack={() => setStep(1)} onNext={onComplete} />}
    </div>
  );
};

// D7 preview (2026-02-14) — export Step1 as a named export so the
// operator-facing sandbox at /preview/kg-hover can mount the REAL
// Step 1 component (no mirror) next to the hover for like-for-like
// size comparison. Safe to keep: Step1 is presentational + takes only
// an `onNext` prop.
export { Step1 as WalkthroughStep1 };

export default Walkthrough;
